"use client";
import Rive from "@rive-app/react-canvas";
import DynamicFlag from "@ui/components/languages/DynamicFlag";
import { cn } from "@ui/lib/utils";
import { gsap } from "gsap";
import { AnimatePresence, m } from "motion/react";
import { useTranslations } from "next-intl";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHover } from "react-aria";

import { NativeAudioProvider, useNativeAudio } from "@/app/_providers/native-audio/NativeAudioProvider";
import { PlayPauseButton } from "@/app/_ui/components/core/PlayPauseButton";

const TypeGraphicOne = ({ className }: { className?: string }) => {
  return (
    <svg className={className} fill='none' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M93.8861 110.909H108.493L67.7294 9H51.7636L11 110.909H25.607L36.4773 83.7334H83.0158L93.8861 110.909ZM41.9124 70.1455L59.7126 25.5772L77.5806 70.1455H41.9124Z'
        fill='#F2F2F2'
      />
      <path
        d='M93.8861 110.909H108.493L67.7294 9H51.7636L11 110.909H25.607L36.4773 83.7334H83.0158L93.8861 110.909ZM41.9124 70.1455L59.7126 25.5772L77.5806 70.1455H41.9124Z'
        fill='url(#paint0_linear_1153_143532)'
      />
      <defs>
        <linearGradient
          gradientUnits='userSpaceOnUse'
          id='paint0_linear_1153_143532'
          x1='82.3364'
          x2='14.541'
          y1='51.7048'
          y2='114.719'
        >
          <stop stopColor='#F5F3F1' />
          <stop offset='1' stopColor='white' />
        </linearGradient>
      </defs>
    </svg>
  );
};

const TypeGraphicTwo = ({ className }: { className?: string }) => {
  return (
    <svg className={className} fill='none' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M113.503 33.5946V20.1568H66.4703V0H53.0325V20.1568H6V33.5946H78.1612C74.4385 48.9498 67.0886 63.1888 56.7279 75.1176C49.974 66.7425 44.7641 57.2326 41.3415 47.0325H27.2318C31.4612 60.7163 38.3067 73.4491 47.3886 84.5241C37.3513 94.2602 25.5961 102.051 12.7189 107.503L17.7581 120C32.2278 113.89 45.4063 105.09 56.5935 94.0649C67.5778 105.24 80.7539 114.025 95.2945 119.866L100.065 107.503C87.1136 102.497 75.3838 94.776 65.664 84.86C78.5279 70.199 87.5748 52.5899 92.0022 33.5946H113.503Z'
        fill='#F2F2F2'
      />
      <path
        d='M113.503 33.5946V20.1568H66.4703V0H53.0325V20.1568H6V33.5946H78.1612C74.4385 48.9498 67.0886 63.1888 56.7279 75.1176C49.974 66.7425 44.7641 57.2326 41.3415 47.0325H27.2318C31.4612 60.7163 38.3067 73.4491 47.3886 84.5241C37.3513 94.2602 25.5961 102.051 12.7189 107.503L17.7581 120C32.2278 113.89 45.4063 105.09 56.5935 94.0649C67.5778 105.24 80.7539 114.025 95.2945 119.866L100.065 107.503C87.1136 102.497 75.3838 94.776 65.664 84.86C78.5279 70.199 87.5748 52.5899 92.0022 33.5946H113.503Z'
        fill='url(#paint0_linear_1153_143531)'
      />
      <defs>
        <linearGradient
          gradientUnits='userSpaceOnUse'
          id='paint0_linear_1153_143531'
          x1='99.9439'
          x2='33.6043'
          y1='55.1613'
          y2='99.197'
        >
          <stop stopColor='white' />
          <stop offset='1' stopColor='#F5F3F1' />
        </linearGradient>
      </defs>
    </svg>
  );
};

const LanguageTag = forwardRef<
  HTMLElement,
  {
    animationDelay: number;
    className?: string;
    icon: React.ReactNode;
    isCompact?: boolean;
    language: string;
    onClick?: () => void;
    position: { bottom?: string; left?: string; right?: string; top?: string };
    variant: "bottomLeft" | "bottomRight" | "topLeft" | "topRight";
  }
>(({ className, icon, isCompact, language, animationDelay: _animationDelay, onClick, position, variant }, ref) => {
  // Define corner radius styles for each variant using CSS custom properties
  const getVariantStyle = (variant: string) => {
    const baseRadius = "30px"; // equivalent to rounded-4xl
    const sharpRadius = "10px"; // equivalent to rounded-8

    switch (variant) {
      case "topLeft":
        return {
          borderTopLeftRadius: baseRadius,
          borderTopRightRadius: baseRadius,
          borderBottomLeftRadius: baseRadius,
          borderBottomRightRadius: sharpRadius, // sharp corner pointing to globe
        };
      case "topRight":
        return {
          borderTopLeftRadius: baseRadius,
          borderTopRightRadius: baseRadius,
          borderBottomLeftRadius: sharpRadius, // sharp corner pointing to globe
          borderBottomRightRadius: baseRadius,
        };
      case "bottomLeft":
        return {
          borderTopLeftRadius: baseRadius,
          borderTopRightRadius: sharpRadius, // sharp corner pointing to globe
          borderBottomLeftRadius: baseRadius,
          borderBottomRightRadius: baseRadius,
        };
      case "bottomRight":
        return {
          borderTopLeftRadius: sharpRadius, // sharp corner pointing to globe
          borderTopRightRadius: baseRadius,
          borderBottomLeftRadius: baseRadius,
          borderBottomRightRadius: baseRadius,
        };
      default:
        return {
          borderRadius: baseRadius,
        };
    }
  };

  const sharedClassName = cn(
    "tw-absolute tw-z-10 tw-flex tw-items-center tw-border tw-border-black/10 tw-bg-white",
    isCompact
      ? "tw-gap-2 tw-p-1 tw-pr-2 md:tw-gap-2 md:tw-p-2 md:tw-pr-3"
      : "tw-gap-2 tw-p-1 tw-pr-4 md:tw-gap-2 md:tw-p-3 md:tw-pr-5",
    onClick && "tw-cursor-pointer tw-outline-none focus-visible:tw-outline-focus",
    className,
  );

  const sharedStyle = {
    opacity: 0, // Start hidden, GSAP will control visibility
    boxShadow: "0 0.125rem 0.0625rem 0 rgba(0, 0, 0, 0.05)",
    ...position,
    ...getVariantStyle(variant),
  };

  const children = (
    <>
      {icon}
      <div className='f-paragraph-03'>{language}</div>
    </>
  );

  if (onClick) {
    return (
      <button
        type='button'
        className={sharedClassName}
        ref={ref as React.Ref<HTMLButtonElement>}
        style={sharedStyle}
        onClick={onClick}
      >
        {children}
      </button>
    );
  }

  return (
    <div className={sharedClassName} ref={ref as React.Ref<HTMLDivElement>} style={sharedStyle}>
      {children}
    </div>
  );
});

LanguageTag.displayName = "LanguageTag";

// Pure function to determine variant based on position - extracted outside component
const getVariantFromPosition = (position: { bottom?: string; left?: string; right?: string; top?: string }) => {
  const hasLeft = position.left !== undefined;
  const hasRight = position.right !== undefined;
  const topValue = position.top ? parseInt(position.top) : 0;

  // Determine if it's in top or bottom half (50% threshold)
  const isTop = topValue < 40;

  if (hasLeft && isTop) return "topLeft";
  if (hasRight && isTop) return "topRight";
  if (hasLeft && !isTop) return "bottomLeft";
  if (hasRight && !isTop) return "bottomRight";

  // Default fallback
  return "topLeft";
};

export interface LanguageAudioSample {
  locale: string;
  audioUrl: string;
}

export interface GlobeProps {
  /** Audio samples for each language, keyed by locale */
  languageAudioSamples?: LanguageAudioSample[];
  /** Locale code of the selected language to exclude from cycling (e.g., "en", "zh") */
  selectedLocale?: string;
}

type GlobeCenterLanguageCardProps = {
  selectedLanguageDetails: { locale: string; language: string };
  selectedAudioUrl: string | null;
  isPlaying: boolean;
  onPlayPause: () => void;
  clickToPreviewLabel: string;
};

/** Owns hover state so it remounts (and resets hover) when `key` locale changes — no sync effect needed. */
function GlobeCenterLanguageCard({
  selectedLanguageDetails,
  selectedAudioUrl,
  isPlaying,
  onPlayPause,
  clickToPreviewLabel,
}: GlobeCenterLanguageCardProps) {
  const { isHovered: overlayHovered, hoverProps } = useHover({});

  return (
    <m.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={cn(
        "tw-pointer-events-auto tw-bg-white/95 tw-backdrop-blur-sm border-black/10 tw-pr-4 flex items-center gap-12 rounded-full border py-10 pl-10 shadow-lg",
        selectedAudioUrl && "hover:shadow-xl cursor-pointer transition-shadow",
      )}
      onPointerEnter={hoverProps.onPointerEnter}
      onPointerLeave={hoverProps.onPointerLeave}
      onClick={selectedAudioUrl ? onPlayPause : undefined}
    >
      <div className={cn("tw-rounded-full tw-overflow-hidden tw-flex-shrink-0 h-36 w-36")}>
        <DynamicFlag
          size={null}
          className='tw-w-full tw-h-full tw-object-cover'
          locale={selectedLanguageDetails.locale}
        />
      </div>

      <div className='tw-flex tw-flex-col'>
        <span className='text-black f-paragraph-02 font-medium'>{selectedLanguageDetails.language}</span>
        {selectedAudioUrl && <span className='tw-text-black/50 f-paragraph-04'>{clickToPreviewLabel}</span>}
      </div>

      {selectedAudioUrl && (
        <PlayPauseButton
          isPlaying={isPlaying}
          variant={overlayHovered ? "primary" : "elevated"}
          size='pill'
          onClick={e => {
            e.stopPropagation();
            onPlayPause();
          }}
        />
      )}
    </m.div>
  );
}

export function Globe(props: GlobeProps) {
  return (
    <NativeAudioProvider>
      <GlobeInner {...props} />
    </NativeAudioProvider>
  );
}

/** Globe without its own NativeAudioProvider — use when a parent provider already exists. */
export function GlobeInner({ selectedLocale: selectedLocaleProp, languageAudioSamples }: GlobeProps) {
  const globeRef = useRef<HTMLDivElement>(null);
  const tagsRef = useRef<HTMLElement[]>([]);
  const t = useTranslations("ClientKeys.v3.globe");

  const { togglePlay, playingId, play, stop } = useNativeAudio();

  // Internal selected locale state (used when not externally controlled)
  const [internalSelectedLocale, setInternalSelectedLocale] = useState<string | undefined>();
  const selectedLocale = selectedLocaleProp ?? internalSelectedLocale;

  // Derive playing state from provider
  const isPlaying = selectedLocale ? playingId === `globe-${selectedLocale}` : false;

  // Stop audio when externally-controlled locale changes
  const prevExternalLocaleRef = useRef(selectedLocaleProp);
  useEffect(() => {
    if (prevExternalLocaleRef.current !== selectedLocaleProp && prevExternalLocaleRef.current !== undefined) {
      stop();
    }
    prevExternalLocaleRef.current = selectedLocaleProp;
  }, [selectedLocaleProp, stop]);

  // Language configuration with positions and locales
  const languages = useMemo(
    () => [
      { locale: "en", position: { top: "18%", left: "3%" }, language: t("languages.english") },
      { locale: "zh", position: { top: "60%", left: "0%" }, language: t("languages.chinese") },
      { locale: "es", position: { top: "25%", right: "0%" }, language: t("languages.spanish") },
      { locale: "fr", position: { top: "20%", left: "2%" }, language: t("languages.french") },
      { locale: "pt", position: { top: "68%", right: "0%" }, language: t("languages.portuguese") },
      { locale: "de", position: { top: "50%", left: "-3%" }, language: t("languages.german") },
      { locale: "ja", position: { top: "20%", right: "4%" }, language: t("languages.japanese") },
      { locale: "it", position: { top: "52%", right: "0%" }, language: t("languages.italian") },
      { locale: "hi", position: { top: "68%", left: "0%" }, language: t("languages.hindi") },
    ],
    [t],
  );

  // Find selected language details for the overlay
  const selectedLanguageDetails = useMemo(() => {
    if (!selectedLocale) return null;
    return languages.find(lang => lang.locale === selectedLocale) || null;
  }, [selectedLocale, languages]);

  // Find audio URL for selected locale
  const selectedAudioUrl = useMemo(() => {
    if (!selectedLocale || !languageAudioSamples) return null;
    const sample = languageAudioSamples.find(s => s.locale === selectedLocale);
    return sample?.audioUrl || null;
  }, [selectedLocale, languageAudioSamples]);

  // Handle play/pause for the centered overlay
  const handlePlayPause = useCallback(() => {
    if (!selectedAudioUrl || !selectedLocale) return;
    togglePlay({ id: `globe-${selectedLocale}`, url: selectedAudioUrl });
  }, [selectedAudioUrl, selectedLocale, togglePlay]);

  // Handle tag click (only when not externally controlled)
  const handleTagClick = useCallback(
    (locale: string) => {
      if (selectedLocaleProp !== undefined) return;

      if (internalSelectedLocale === locale) {
        stop();
        setInternalSelectedLocale(undefined);
        return;
      }

      setInternalSelectedLocale(locale);
      const sample = languageAudioSamples?.find(s => s.locale === locale);
      if (sample) {
        play({ id: `globe-${locale}`, url: sample.audioUrl });
      }
    },
    [selectedLocaleProp, internalSelectedLocale, languageAudioSamples, play, stop],
  );

  useEffect(() => {
    const globe = globeRef.current;
    const tags = tagsRef.current.filter(Boolean);
    if (!globe || !tags.length) return;

    // Initialize ALL tags to opacity 0 and scale 0.95
    gsap.set(tags, { x: 0, y: 0, rotation: 0, opacity: 0, scale: 0.95 });

    // Set transform origin for each tag based on its variant
    tags.forEach((tag, index) => {
      const lang = languages[index];
      const variant = getVariantFromPosition(lang.position);

      // Set transform origin to where the sharp corner is
      const transformOrigins = {
        topLeft: "bottom right", // sharp corner is bottom-right
        topRight: "bottom left", // sharp corner is bottom-left
        bottomLeft: "top right", // sharp corner is top-right
        bottomRight: "top left", // sharp corner is top-left
      };

      gsap.set(tag, { transformOrigin: transformOrigins[variant] });
    });

    // Track which language is shown at each position
    const currentLanguageAtPosition = [0, 1, 2]; // Start with first 3 languages
    let nextLanguageIndex = 3; // Next language to show

    // Show initial 3 tags with scale animation
    currentLanguageAtPosition.forEach(langIndex => {
      if (tags[langIndex]) {
        gsap.to(tags[langIndex], {
          opacity: 1,
          scale: 1,
          duration: 0.4,
          ease: "back.out(1.2)",
        });
      }
    });

    const quickTos = tags.map(tag => ({
      x: gsap.quickTo(tag, "x", { duration: 0.6, ease: "power2.out" }),
      y: gsap.quickTo(tag, "y", { duration: 0.6, ease: "power2.out" }),
    }));

    const onMove = (e: MouseEvent) => {
      const rect = globe.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) / rect.width; // -0.5 to 0.5
      const y = (e.clientY - rect.top - rect.height / 2) / rect.height; // -0.5 to 0.5

      // Only animate currently visible tags
      currentLanguageAtPosition.forEach(langIndex => {
        if (tags[langIndex] && quickTos[langIndex]) {
          quickTos[langIndex].x(x * 10);
          quickTos[langIndex].y(y * 10);
        }
      });
    };

    const onLeave = () => {
      currentLanguageAtPosition.forEach(langIndex => {
        if (quickTos[langIndex]) {
          quickTos[langIndex].x(0);
          quickTos[langIndex].y(0);
        }
      });
    };

    // Rotating animation - one tag at a time
    let currentPositionToRotate = 0;
    const rotateTag = () => {
      const oldLanguageIndex = currentLanguageAtPosition[currentPositionToRotate];
      const newLanguageIndex = nextLanguageIndex % languages.length;

      // Ensure we're not showing the same language in multiple positions
      if (currentLanguageAtPosition.includes(newLanguageIndex)) {
        nextLanguageIndex = (nextLanguageIndex + 1) % languages.length;
        return;
      }

      // Hide ALL tags first to ensure clean state
      tags.forEach((tag, index) => {
        if (!currentLanguageAtPosition.includes(index) || index === oldLanguageIndex) {
          gsap.set(tag, { opacity: 0, scale: 0.95 });
        }
      });

      // Fade out and scale down old tag
      if (tags[oldLanguageIndex]) {
        gsap.to(tags[oldLanguageIndex], {
          opacity: 0,
          scale: 0.95,
          duration: 0.4,
          ease: "power2.inOut",
        });
      }

      // Update tracking immediately
      currentLanguageAtPosition[currentPositionToRotate] = newLanguageIndex;

      // Fade in and scale up new tag
      if (tags[newLanguageIndex]) {
        gsap.to(tags[newLanguageIndex], {
          opacity: 1,
          scale: 1,
          duration: 0.4,
          ease: "back.out(1.2)",
          delay: 0.2,
        });
      }

      // Move to next position and language
      nextLanguageIndex = (nextLanguageIndex + 1) % languages.length;
      currentPositionToRotate = (currentPositionToRotate + 1) % 3;
    };

    // Start rotating after initial display
    const rotateInterval = setInterval(rotateTag, 4000); // Rotate every 4 seconds

    globe.addEventListener("mousemove", onMove);
    globe.addEventListener("mouseleave", onLeave);

    return () => {
      globe.removeEventListener("mousemove", onMove);
      globe.removeEventListener("mouseleave", onLeave);
      clearInterval(rotateInterval);
    };
  }, []);

  return (
    <div className='relative mx-auto aspect-1x1 w-full max-w-[500px] p-52' ref={globeRef}>
      <TypeGraphicOne className='absolute right-28 top-28 h-[20%] w-[20%] sm:h-[25%] sm:w-[25%] md:h-[22%] md:w-[22%] lg:h-[20%] lg:w-[20%]' />
      <TypeGraphicTwo className='absolute bottom-28 left-28 h-[20%] w-[20%] sm:h-[25%] sm:w-[25%] md:h-[22%] md:w-[22%] lg:h-[20%] lg:w-[20%]' />

      {languages.map((lang, index) => {
        const isClickable =
          selectedLocaleProp === undefined && languageAudioSamples?.some(s => s.locale === lang.locale);

        return (
          <LanguageTag
            animationDelay={0}
            className=''
            icon={<DynamicFlag className='h-10 w-10 rounded-full md:h-12 md:w-12' locale={lang.locale} />}
            isCompact
            key={lang.locale}
            language={lang.language}
            onClick={isClickable ? () => handleTagClick(lang.locale) : undefined}
            position={lang.position}
            ref={(el: HTMLElement | null) => {
              if (el) tagsRef.current[index] = el;
            }}
            variant={getVariantFromPosition(lang.position)}
          />
        );
      })}

      <div className='pointer-events-none relative z-1 mx-auto aspect-1x1 w-full rotate-[-54deg] rounded-[50%] bg-default'>
        <Rive
          className='absolute inset-0 z-2 opacity-80'
          src='https://storage.googleapis.com/eleven-public-cdn/marketing_website/assets/convai/globe_2.riv'
        />
      </div>

      {/* Centered selected language overlay */}
      {selectedLanguageDetails && (
        <div className='tw-pointer-events-none tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center'>
          <AnimatePresence mode='wait'>
            <GlobeCenterLanguageCard
              key={selectedLocale}
              selectedLanguageDetails={selectedLanguageDetails}
              selectedAudioUrl={selectedAudioUrl}
              isPlaying={isPlaying}
              onPlayPause={handlePlayPause}
              clickToPreviewLabel={t("click_to_preview")}
            />
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
